import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Bell, Check, Eye, X } from "lucide-react";
import { toast } from "react-toastify";
import { RootState } from "../../../redux/store";
import api from "../../../api/apiClient";
import { apiPut } from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/alert";
import { formatNumber } from "../../../utils/formatNumber";

interface IncomingOrderAlertItem {
    orderId: number;
    orderNumber?: number;
    productCount?: number;
    finalPriceToPay?: number;
    autoAccept?: boolean;
}

interface IncomingOrderEventDetail {
    orderId?: number;
    orderNumber?: number;
    productCount?: number;
    finalPriceToPay?: number;
    status?: string;
    autoAccept?: boolean;
    order?: {
        id?: number;
        orderNumber?: number;
        status?: string;
        finalPriceToPay?: number;
        orderItems?: { quantity?: number }[];
    };
}

const IncomingOrderAlertManager: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const role = useSelector((state: RootState) => state.auth.role);
    const store = useSelector((state: RootState) => state.store?.store);
    const numericStoreId = store?.id;

    const [pendingOrders, setPendingOrders] = useState<IncomingOrderAlertItem[]>([]);
    const seenOrderIdsRef = useRef<Set<number>>(new Set());
    const soundIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const incomingOrderAudioRef = useRef<HTMLAudioElement | null>(null);

    const stopIncomingOrderSound = useCallback(() => {
        if (soundIntervalRef.current) {
            clearInterval(soundIntervalRef.current);
            soundIntervalRef.current = null;
        }

        if (incomingOrderAudioRef.current) {
            incomingOrderAudioRef.current.pause();
            incomingOrderAudioRef.current.currentTime = 0;
        }
    }, []);

    const playIncomingOrderSound = useCallback(() => {
        try {
            if (!incomingOrderAudioRef.current) {
                incomingOrderAudioRef.current = new Audio("/car_horn.mp3");
                incomingOrderAudioRef.current.preload = "auto";
            }

            incomingOrderAudioRef.current.currentTime = 0;
            void incomingOrderAudioRef.current.play();
        } catch (error) {
            console.error("IncomingOrderAlertManager sound failed:", error);
        }
    }, []);

    const startIncomingOrderSound = useCallback(() => {
        if (soundIntervalRef.current) return;
        playIncomingOrderSound();
        soundIntervalRef.current = setInterval(playIncomingOrderSound, 1600);
    }, [playIncomingOrderSound]);

    const dismissSingle = useCallback(
        (orderId: number) => {
            toast.dismiss(`incoming-order-alert-${orderId}`);
            setPendingOrders((prev) => {
                const next = prev.filter((item) => item.orderId !== orderId);
                if (next.length === 0) {
                    seenOrderIdsRef.current.clear();
                    stopIncomingOrderSound();
                }
                return next;
            });
        },
        [stopIncomingOrderSound]
    );

    const handleViewOrder = useCallback(
        (orderId: number) => {
            dismissSingle(orderId);
            navigate(`/store/orders/${orderId}`);
        },
        [dismissSingle, navigate]
    );

    const handleAcceptOrder = useCallback(
        async (orderId: number) => {
            if (!numericStoreId) {
                showError(t("store_not_found") || "Store not found");
                return;
            }

            try {
                await apiPut(`order/accept/${orderId}?storeId=${numericStoreId}`, { extraMinutes: 5 });
                showSuccess(t("order_accepted_success") || "Order accepted successfully");
                dismissSingle(orderId);
                await queryClient.invalidateQueries({ queryKey: ["incoming-orders-count"] });
            } catch (error: any) {
                showError(error?.message || t("error_occurred") || "Something went wrong");
            }
        },
        [dismissSingle, numericStoreId, queryClient, t]
    );

    const handleRejectOrder = useCallback(
        async (orderId: number) => {
            if (!numericStoreId) {
                showError(t("store_not_found") || "Store not found");
                return;
            }

            try {
                await apiPut(`order/reject/${orderId}?storeId=${numericStoreId}`, {});

                showSuccess(t("order_rejected_success") || "Order rejected successfully");

                dismissSingle(orderId);

                await queryClient.invalidateQueries({
                    queryKey: ["incoming-orders-count"],
                });
            } catch (error: any) {
                showError(error?.message || t("error_occurred") || "Something went wrong");
            }
        },
        [dismissSingle, numericStoreId, queryClient, t]
    );

    const addIncomingOrderAlert = useCallback((payload?: IncomingOrderEventDetail) => {
        if (typeof payload?.orderId !== "number" || !Number.isFinite(payload.orderId)) return false;

        const orderId = payload.orderId;
        if (seenOrderIdsRef.current.has(orderId)) return false;
        seenOrderIdsRef.current.add(orderId);

        setPendingOrders((prev) => {
            const exists = prev.some((item) => item.orderId === orderId);
            if (exists) return prev;

            return [
                ...prev,
                {
                    orderId,
                    orderNumber: payload.orderNumber,
                    productCount: payload.productCount,
                    finalPriceToPay: payload.finalPriceToPay,
                    autoAccept: payload.autoAccept,
                },
            ];
        });

        return true;
    }, []);

    const showIncomingOrderToast = useCallback(
        (order: IncomingOrderAlertItem) => {
            toast.info(
                <div className="w-full max-w-md rounded-3xl bg-white text-slate-900 shadow-2xl ring-1 ring-slate-200/80">
                    <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-4">
                        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                            <Bell className="h-5 w-5" />
                        </span>
                        <div>
                            <p className="text-sm font-semibold text-slate-900">{t("new_order") || "NEW ORDER"}</p>
                            <p className="text-xs text-slate-500">#{formatNumber(order.orderNumber ?? order.orderId)}</p>
                        </div>
                    </div>
                    <div className="space-y-2 px-4 py-3 text-sm text-slate-700">
                        <div className="flex items-center justify-between">
                            <span>{t("items") || "Items"}</span>
                            <span className="font-semibold">{order.productCount ?? 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span>{t("total_amount") || "Total"}</span>
                            <span className="font-semibold">{order.finalPriceToPay ? formatNumber(order.finalPriceToPay) : "0"} SAR</span>
                        </div>
                    </div>
                    <div className="border-t border-slate-100 px-4 py-3">
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => handleViewOrder(order.orderId)}
                                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primaryButton px-3 py-2.5 text-xs font-semibold text-white transition hover:opacity-90"
                            >
                                <Eye size={14} />
                                {t("view_order") || "View Order"}
                            </button>

                            {!order.autoAccept && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => handleAcceptOrder(order.orderId)}
                                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
                                    >
                                        <Check size={14} />
                                        {t("accept") || "Accept"}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => handleRejectOrder(order.orderId)}
                                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-600 px-3 py-2.5 text-xs font-semibold text-white transition hover:bg-red-700"
                                    >
                                        <X size={14} />
                                        {t("reject") || "Reject"}
                                    </button>
                                </>
                            )}

                            <button
                                type="button"
                                onClick={() => dismissSingle(order.orderId)}
                                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                            >
                                <X size={14} />
                                {t("noted") || "Noted"}
                            </button>
                        </div>
                    </div>
                </div>,
                {
                    toastId: `incoming-order-alert-${order.orderId}`,
                    autoClose: false,
                    closeButton: false,
                    draggable: false,
                    position: "top-center",
                    hideProgressBar: true,
                    icon: false,
                    className: "bg-transparent shadow-none p-0",
                }
            );
        },
        [dismissSingle, handleAcceptOrder, handleViewOrder, handleRejectOrder,t]
    );

    useEffect(() => {
        if (role !== "STORE") return;

        const handleOrderUpdate = async (event: Event) => {
            const detail = (event as CustomEvent<IncomingOrderEventDetail>).detail;
            if (!detail) return;

            const status = detail.status ?? detail.order?.status;
            if (status !== "incoming" && status !== "pending_confirmation") return;

            const orderId = detail.orderId ?? detail.order?.id;
            if (!orderId) return;

            const incomingOrder: IncomingOrderAlertItem = {
                orderId,
                orderNumber: detail.orderNumber ?? detail.order?.orderNumber,
                productCount: detail.productCount,
                finalPriceToPay: detail.finalPriceToPay ?? detail.order?.finalPriceToPay,
                autoAccept: detail.autoAccept,
            };

            if (
                typeof incomingOrder.orderNumber !== "number" ||
                typeof incomingOrder.productCount !== "number" ||
                typeof incomingOrder.finalPriceToPay !== "number"
            ) {
                try {
                    const orderDetail = await api.get<{ orderNumber: number; finalPriceToPay: number; orderItems: { quantity?: number }[] }>(
                        `order/${orderId}/byStore${numericStoreId ? `?storeId=${numericStoreId}` : ""}`
                    );
                    incomingOrder.orderNumber = orderDetail.data.orderNumber;
                    incomingOrder.finalPriceToPay = orderDetail.data.finalPriceToPay;
                    incomingOrder.productCount = orderDetail.data.orderItems?.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
                } catch (error) {
                    console.error("IncomingOrderAlertManager: failed to load order details", error);
                }
            }

            const added = addIncomingOrderAlert(incomingOrder);
            if (added) {
                showIncomingOrderToast(incomingOrder);
            }
        };

        window.addEventListener("orderUpdated", handleOrderUpdate as EventListener);
        return () => {
            window.removeEventListener("orderUpdated", handleOrderUpdate as EventListener);
        };
    }, [addIncomingOrderAlert, numericStoreId, role, showIncomingOrderToast]);

    useEffect(() => {
        if (pendingOrders.length === 0) {
            stopIncomingOrderSound();
            return;
        }

        startIncomingOrderSound();
    }, [pendingOrders.length, startIncomingOrderSound, stopIncomingOrderSound]);

    useEffect(() => {
        return () => {
            stopIncomingOrderSound();
        };
    }, [stopIncomingOrderSound]);

    return null;
};

export default IncomingOrderAlertManager;
